import * as turf from "@turf/turf";
import axios from "axios";
import { sidewalkColors, hexaColors } from "./MapStyles";
import { addMouseevents } from "./Routing";

export async function apiCall(options) {
    let phpUrl = process.env.PUBLIC_URL + "/php/api.php";
    if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
        phpUrl = "http://localhost/route4u_survey/hive/public/php/api.php";
    }

    let postData = new FormData();

    for (const key in options) {
        postData.append(key, options[key]);
    }

    const response = await axios.post(phpUrl, postData, {
        headers: {
            "Content-Type": "application/json",
        },
    });
    return response.data;
}

async function loadObstacleImages(map) {
    const images = [
        {
            url: process.env.PUBLIC_URL + "/img/obstacle_red.png",
            id: "BLOCKED",
        },
        {
            url: process.env.PUBLIC_URL + "/img/obstacle_orange.png",
            id: "ROUGH",
        },
        {
            url: process.env.PUBLIC_URL + "/img/obstacle_yellow.png",
            id: "LIMITED",
        },
    ];

    await Promise.all(
        images.map(
            (img) =>
                new Promise((resolve, reject) => {
                    map.loadImage(img.url, function (error, res) {
                        map.addImage(img.id, res);
                        resolve();
                    });
                })
        )
    );
    return;
}

async function loadRouteImages(map) {
    const images = [
        { url: process.env.PUBLIC_URL + "/img/start.png", id: "start" },
        { url: process.env.PUBLIC_URL + "/img/stop.png", id: "stop" },
    ];

    await Promise.all(
        images.map(
            (img) =>
                new Promise((resolve, reject) => {
                    map.loadImage(img.url, function (error, res) {
                        map.addImage(img.id, res);
                        resolve();
                    });
                })
        )
    );
    return;
}

function loadObstacles(map, bottom, left, top, right) {
    const params = new URLSearchParams({
        bottomLeft: [bottom, left].join(","),
        topRight: [top, right].join(","),
    });
    const options = { service: "obstacle", lang: "en", params: params };

    apiCall(options)
        .then((obstacles) => {
            obstacles = JSON.parse(JSON.stringify(obstacles));
            Promise.all(
                obstacles.result.map((obstacle) => {
                    return new Promise((resolve, reject) => {
                        const options = {
                            service: "obstacle/" + obstacle.id + "/",
                            lang: "en",
                        };
                        apiCall(options)
                            .then((data) => {
                                data = JSON.parse(JSON.stringify(data));
                                let accessibilityText = "";
                                switch (obstacle.accessibility) {
                                    case "BLOCKED":
                                        accessibilityText = "not accessible";
                                        break;
                                    case "ROUGH":
                                        accessibilityText = "needs assistance";
                                        break;
                                    case "LIMITED":
                                        accessibilityText = "uncomfortable";
                                        break;
                                }
                                const obstacle_img = data.result.imageUrl
                                    ? "<img src=" +
                                      data.result.imageUrl +
                                      ' class="obstacle-img">'
                                    : "";
                                const lastUpdateDate = new Date(
                                    data.result.lastUpdateDate
                                );
                                const obstacle_info =
                                    '<table class="table"><tbody><tr><td><b>Accessibility:</b></td><td>' +
                                    accessibilityText +
                                    "</td></tr><tr><td><b>Type:</b></td><td>" +
                                    data.result.obstacleCategory.dictionary[
                                        "en"
                                    ] +
                                    "</td></tr><tr><td><b>Last updated:</b></td><td>" +
                                    lastUpdateDate.toLocaleString() +
                                    "</td></tr></tbody></table>";
                                resolve({
                                    type: "Feature",
                                    geometry: {
                                        type: "Point",
                                        coordinates: [
                                            obstacle.lon,
                                            obstacle.lat,
                                        ],
                                    },
                                    properties: {
                                        id: obstacle.id,
                                        accessibility: obstacle.accessibility,
                                        html: obstacle_img + obstacle_info,
                                    },
                                });
                            })
                            .catch((err) => {
                                console.log("Details: " + "\nError:" + err);
                            });
                    });
                })
            ).then((features) => {
                map.getSource("obstacleSource").setData({
                    type: "FeatureCollection",
                    features: features,
                });
            });
        })
        .catch((err) => {
            console.log("Details: " + "\nError:" + err);
        });
}

export function zoomCity(map, thisCity) {
    const boundaryUrl = thisCity.boundary;
    axios
        .get(process.env.PUBLIC_URL + "/data/" + boundaryUrl)
        .then((response) => {
            const bbox = turf.bbox(response.data.features[0]);
            map.fitBounds(bbox, { padding: 24 });
            loadObstacles(map, bbox[1], bbox[0], bbox[3], bbox[2]);
        })
        .catch((errors) => {
            console.log(errors);
        });
}

export function loadData(map, thisCity) {
    const hexGridUrl = thisCity.hexgrid;
    axios
        .get(process.env.PUBLIC_URL + "/data/" + hexGridUrl)
        .then((response) => {
            map.getSource("hexa").setData(response.data);
        })
        .catch((errors) => {
            console.log(errors);
        });
}

function populatePopup(e, tags, popup, map) {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = "crosshair";
    const coordinates = e.lngLat;
    const props = e.features[0].properties;
    const descriptionArray = tags.map((tag) => {
        let key = tag;
        let value = props[tag];
        if (value) {
            if (key === "width" && Number(value) === 1) {
                key = "sidewalk";
                value = "missing";
            }
            if (key === "width" || key === "curb-height") value = value + " cm";

            if (key === "steep") value = String(Math.abs(Number(value))) + "%";
            if (key === "surface") {
                key = "paving material";
                if (value === "gravel") value = "dirt";
            }
            if (key === "steep") key = "incline";
            if (key === "surface-smoothness") {
                switch (value) {
                    case "green":
                        value = "excellent";
                        break;
                    case "yellow":
                        value = "uncomfortable";
                        break;
                    case "orange":
                        value = "very uncomfortable";
                        break;
                    case "red":
                        value = "not accessible";
                        break;

                    default:
                        break;
                }
            }
        }
        return value
            ? "<b>" +
                  key.replace("-", " ") +
                  ":</b> " +
                  value.replace("_", " ") +
                  "<br/>"
            : "";
    });
    const description = descriptionArray.join("");

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup.setLngLat(coordinates).setHTML(description).addTo(map);
}

export function createSidewalkLayer(map, popup) {
    const sourceUrl = "mapbox://route4u.nocurb";
    const sourceLayer = "nocurb";
    map.addSource("sidewalkSource", {
        type: "vector",
        url: sourceUrl,
    });
    map.addLayer(
        {
            id: "sidewalks",
            type: "line",
            source: "sidewalkSource",
            "source-layer": sourceLayer,
            layout: {
                visibility: "none",
            },
            paint: {
                "line-color": "black",
                "line-width": 1,
                "line-opacity": 1,
            },
        },
        "waterway-label"
    );
    map.on("mousemove", "sidewalks", function (e) {
        const tags = [
            "name",
            "surface-smoothness",
            "steep",
            "width",
            "surface",
        ];
        populatePopup(e, tags, popup, map);
    });

    map.on("mouseleave", "sidewalks", function () {
        map.getCanvas().style.cursor = "";
        popup.remove();
    });
}

export function createCurbLayer(map, popup) {
    const sourceUrl = "mapbox://route4u.curb";
    const sourceLayer = "curb";
    map.addSource("curbSource", {
        type: "vector",
        url: sourceUrl,
    });
    map.addLayer(
        {
            id: "curbs",
            type: "line",
            source: "curbSource",
            "source-layer": sourceLayer,
            layout: {
                visibility: "none",
            },
            paint: {
                "line-color": "black",
                "line-width": 4,
                "line-opacity": 1,
            },
            filter: [
                "all",
                ["==", ["get", "side"], "crossing"],
                ["match", ["get", "sidewalk"], ["missing"], false, true],
            ],
        },
        "waterway-label"
    );
    map.on("mousemove", "curbs", function (e) {
        const tags = ["name", "surface", "surface-smoothness", "curb-height"];
        populatePopup(e, tags, popup, map);
    });

    map.on("mouseleave", "curbs", function () {
        map.getCanvas().style.cursor = "";
        popup.remove();
    });
}

export function createObstacleLayer(map, popup) {
    loadObstacleImages(map)
        .then((data) => {
            map.addSource("obstacleSource", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
            map.addLayer({
                id: "obstacles",
                type: "symbol",
                source: "obstacleSource",
                layout: {
                    "icon-image": ["get", "accessibility"],
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                    visibility: "none",
                },
            });

            map.on("mousemove", "obstacles", function (e) {
                // Change the cursor style as a UI indicator.
                map.getCanvas().style.cursor = "crosshair";
                const coordinates = e.lngLat;
                const props = e.features[0].properties;
                const description = props.html;

                // Ensure that if the map is zoomed out such that multiple
                // copies of the feature are visible, the popup appears
                // over the copy being pointed to.
                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] +=
                        e.lngLat.lng > coordinates[0] ? 360 : -360;
                }

                // Populate the popup and set its coordinates
                // based on the feature found.
                popup.setLngLat(coordinates).setHTML(description).addTo(map);
            });

            map.on("mouseleave", "obstacles", function () {
                map.getCanvas().style.cursor = "";
                popup.remove();
            });
        })
        .catch((err) => {
            console.log(err);
        });
}

export async function createRouteLayers(map, popup) {
    await loadRouteImages(map);

    map.addSource("routePoint", {
        type: "geojson",
        data: {
            type: "FeatureCollection",
            features: [],
        },
    });
    map.addLayer({
        id: "point",
        type: "symbol",
        source: "routePoint",
        layout: {
            "icon-image": ["get", "id"],
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
            "icon-size": 0.1,
            visibility: "none",
        },
    });

    map.addSource("route", {
        type: "geojson",
        data: {
            type: "Feature",
            properties: {},
            geometry: {
                type: "LineString",
                coordinates: [],
            },
        },
    });
    map.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {
            "line-join": "round",
            "line-cap": "round",
            visibility: "none",
        },
        paint: {
            "line-color": "rgba(0,0,0,0.1)",
            "line-width": 3,
            "line-dasharray": [1, 2],
        },
    });

    map.addSource("walkRoute", {
        type: "geojson",
        data: {
            type: "FeatureCollection",
            features: [],
        },
    });

    map.addLayer({
        id: "walkRoute",
        type: "line",
        source: "walkRoute",
        layout: {
            "line-join": "round",
            "line-cap": "round",
            visibility: "none",
        },
        paint: {
            "line-color": "blue",
            "line-width": 1,
            "line-opacity": 0.6,
        },
    });

    map.addSource("wheelchairRoute", {
        type: "geojson",
        data: {
            type: "FeatureCollection",
            features: [],
        },
    });

    map.addLayer({
        id: "wheelchairRoute",
        type: "line",
        source: "wheelchairRoute",
        layout: {
            "line-join": "round",
            "line-cap": "round",
            visibility: "none",
        },
        paint: {
            "line-color": "red",
            "line-width": 1,
            "line-opacity": 0.6,
        },
    });

    return;
}

export function setRouteData(map, routeData) {
    let features = [
        {
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: routeData.start,
            },
            properties: { id: "start" },
        },
        {
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: routeData.stop,
            },
            properties: { id: "stop" },
        },
    ];

    let routeFeature = {
        type: "Feature",
        properties: {},
        geometry: {
            type: "LineString",
            coordinates: [
                features[0].geometry.coordinates,
                features[1].geometry.coordinates,
            ],
        },
    };

    map.getSource("routePoint").setData({
        type: "FeatureCollection",
        features: features,
    });

    map.getSource("route").setData(routeFeature);
    addMouseevents(map, features, routeFeature);
}

export function createHexagonLayer(map, popup) {
    map.addSource("hexa", {
        type: "geojson",
        data: {
            type: "FeatureCollection",
            features: [],
        },
    });
    map.addLayer({
        id: "hexagons",
        type: "fill",
        source: "hexa",
        layout: { visibility: "none" },
        paint: {
            "fill-color": "grey",
            "fill-outline-color": "rgba(59,59,182,0.2)",
            "fill-opacity": 0.5,
        },
        filter: ["!=", 0, ["get", "equality"]],
    });
    map.on("mousemove", "hexagons", function (e) {
        // Change the cursor style as a UI indicator.
        map.getCanvas().style.cursor = "crosshair";
        var coordinates = e.lngLat;
        var description =
            "<b>Average walking distance*:</b> " +
            Math.round(e.features[0].properties.walkdistance / 12) +
            "<br/><b>Average wheelchair distance*:</b> " +
            Math.round(e.features[0].properties.wheelchairdistance / 12) +
            "<br/><b>Inclusion index**:</b> " +
            Math.round(e.features[0].properties.equality * 100) +
            "%" +
            "<br/><br/><small>* average distance to traverse 100 m </small> " +
            "<br/><small>** walking / wheelchair distance ratio </small> ";

        if (e.features[0].properties.equality == 0) {
            description = "No route in this area!";
        }
        if (e.features[0].properties.equality == -1) {
            description = "Data loading... Please, wait a sec";
        }

        // Ensure that if the map is zoomed out such that multiple
        // copies of the feature are visible, the popup appears
        // over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        // Populate the popup and set its coordinates
        // based on the feature found.
        popup.setLngLat(coordinates).setHTML(description).addTo(map);
    });
    map.on("mouseleave", "hexagons", function () {
        map.getCanvas().style.cursor = "";
        popup.remove();
    });
}

export function updateMapLayers(map, mapview, mapfilter) {
    try {
        if (map.getLayer("sidewalks")) {
            if (mapview == "props") {
                map.setLayoutProperty("sidewalks", "visibility", "visible");
                map.setPaintProperty(
                    "sidewalks",
                    "line-color",
                    sidewalkColors[mapfilter]
                );
                if (mapfilter == "curbs" || mapfilter == "obstacles") {
                    map.setLayoutProperty("sidewalks", "visibility", "none");
                }
            } else {
                map.setLayoutProperty("sidewalks", "visibility", "none");
            }
        }
        if (map.getLayer("curbs")) {
            if (mapfilter == "curbs" || mapfilter == "roughness") {
                map.setLayoutProperty("curbs", "visibility", "visible");
                map.setPaintProperty(
                    "curbs",
                    "line-color",
                    sidewalkColors[mapfilter]
                );
            } else {
                map.setLayoutProperty("curbs", "visibility", "none");
            }
        }

        if (map.getLayer("obstacles")) {
            if (mapfilter == "obstacles") {
                map.setLayoutProperty("obstacles", "visibility", "visible");
            } else {
                map.setLayoutProperty("obstacles", "visibility", "none");
            }
        }

        if (map.getLayer("hexagons")) {
            if (mapview == "indices") {
                map.setLayoutProperty("hexagons", "visibility", "visible");
                map.setPaintProperty(
                    "hexagons",
                    "fill-color",
                    hexaColors[mapfilter]
                );
            } else {
                map.setLayoutProperty("hexagons", "visibility", "none");
            }
        }
        if (map.getLayer("point")) {
            if (mapview == "routes") {
                map.setLayoutProperty("point", "visibility", "visible");
            } else {
                map.setLayoutProperty("point", "visibility", "none");
            }
        }

        if (map.getLayer("route")) {
            if (mapview == "routes") {
                map.setLayoutProperty("route", "visibility", "visible");
            } else {
                map.setLayoutProperty("route", "visibility", "none");
            }
        }
        if (map.getLayer("walkRoute")) {
            if (mapview == "routes") {
                map.setLayoutProperty("walkRoute", "visibility", "visible");
            } else {
                map.setLayoutProperty("walkRoute", "visibility", "none");
            }
        }
        if (map.getLayer("wheelchairRoute")) {
            if (mapview == "routes") {
                map.setLayoutProperty(
                    "wheelchairRoute",
                    "visibility",
                    "visible"
                );
            } else {
                map.setLayoutProperty("wheelchairRoute", "visibility", "none");
            }
        }
    } catch (error) {
        console.log(error);
    }
}
